import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";

interface ILeadTimeOptions {
  leadTimeDays: number | undefined;
  upperBoundLeadTimeDays: number | undefined;
  lowerBoundLeadTimeDays: number | undefined;
}

export const buildLeadTime = (
  {
    leadTimeDays,
    upperBoundLeadTimeDays,
    lowerBoundLeadTimeDays,
  }: ILeadTimeOptions,
  strictLocalize: StrictLocalizeFunction
): string | null => {
  const hasLeadTimeAsRange =
    upperBoundLeadTimeDays !== lowerBoundLeadTimeDays &&
    isNotUndefined(lowerBoundLeadTimeDays) &&
    isNotUndefined(upperBoundLeadTimeDays);

  if (hasLeadTimeAsRange) {
    return strictLocalize(
      {
        defaultMessage:
          "{lowerBoundLeadTimeNumber}-{upperBoundLeadTimeNumber} days",
        description: {
          text: "Information on brand fulfillment time, in the form of range (e.g '5-7 days')",
        },
      },
      {
        lowerBoundLeadTimeNumber: lowerBoundLeadTimeDays,
        upperBoundLeadTimeNumber: upperBoundLeadTimeDays,
      }
    );
  }

  if (leadTimeDays) {
    return strictLocalize(
      {
        defaultMessage:
          "{leadTimeDaysNumber, plural, one {a day} other {# days}}",
        description: {
          text: "Information on brand fulfillment time (e.g 'a day' or '5 days')",
        },
      },
      { leadTimeDaysNumber: leadTimeDays }
    );
  }

  return null;
};

export const buildFulfillmentTime = (
  {
    leadTimeDays,
    upperBoundLeadTimeDays,
    lowerBoundLeadTimeDays,
  }: ILeadTimeOptions,
  strictLocalize: StrictLocalizeFunction
): string | null => {
  const hasLeadTimeAsRange =
    upperBoundLeadTimeDays !== lowerBoundLeadTimeDays &&
    isNotUndefined(lowerBoundLeadTimeDays) &&
    isNotUndefined(upperBoundLeadTimeDays);

  if (hasLeadTimeAsRange) {
    return strictLocalize(
      {
        defaultMessage:
          "Fulfills orders within {lowerBoundLeadTimeNumber}-{upperBoundLeadTimeNumber} days",
        description: {
          text: "Information on brand fulfillment time, in the form of range (e.g '5-7 days')",
        },
      },
      {
        lowerBoundLeadTimeNumber: lowerBoundLeadTimeDays,
        upperBoundLeadTimeNumber: upperBoundLeadTimeDays,
      }
    );
  }

  if (leadTimeDays) {
    return strictLocalize(
      {
        defaultMessage:
          "{leadTimeDaysNumber, plural, one {Fulfills orders within a day} other {Fulfills orders within # days}}",
        description: {
          text: "Information on brand fulfillment time",
        },
      },
      { leadTimeDaysNumber: leadTimeDays }
    );
  }

  return null;
};

export const buildFulfillmentExplanation = (
  {
    leadTimeDays,
    upperBoundLeadTimeDays,
    lowerBoundLeadTimeDays,
  }: ILeadTimeOptions,
  strictLocalize: StrictLocalizeFunction
): string => {
  const hasLeadTimeAsRange =
    upperBoundLeadTimeDays !== lowerBoundLeadTimeDays &&
    isNotUndefined(lowerBoundLeadTimeDays) &&
    isNotUndefined(upperBoundLeadTimeDays);

  if (hasLeadTimeAsRange) {
    return strictLocalize(
      {
        defaultMessage:
          "Estimated based on purchase date, brand location, brand lead time ({lowerBoundLeadTimeNumber}-{upperBoundLeadTimeNumber} days), and your shipping location.",
        description: {
          text: "Explaining how the estimated shipping date (in the form if rage, e.g '5-7 days') is calculated.",
        },
      },
      {
        lowerBoundLeadTimeNumber: lowerBoundLeadTimeDays,
        upperBoundLeadTimeNumber: upperBoundLeadTimeDays,
      }
    );
  }

  if (leadTimeDays) {
    return strictLocalize(
      {
        defaultMessage:
          "{leadTimeDaysNumber, plural, one {Estimated based on purchase date, brand location, brand lead time (a day), and your shipping location.} other {Estimated based on purchase date, brand location, brand lead time (# days), and your shipping location.}}",
        description: {
          text: "Explaining how the estimated shipping date is calculated.",
        },
      },
      { leadTimeDaysNumber: leadTimeDays }
    );
  }

  return strictLocalize({
    defaultMessage:
      "Estimated based on purchase date, brand location, brand lead time, and your shipping location.",
    description: {
      text: "Explaining how the estimated shipping date is calculated.",
    },
  });
};
